import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CableOutlinedIcon from "@mui/icons-material/CableOutlined";
import ElectricalServicesOutlinedIcon from "@mui/icons-material/ElectricalServicesOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PriceCheckOutlinedIcon from "@mui/icons-material/PriceCheckOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Link as RouterLink, Navigate } from "react-router-dom";

import DistributionReviewApi from "../../../../infrastructure/api/distributionReview";
import { canManageDistributionTeam } from "../../../utils/authRoleHelpers";
import DistributionCompanySelect, {
  getSelectedDistributionCompanyId,
  isSystemAdminContext,
} from "../DistributionCompanySelect";
import EDCBreadcrumbs, { EDC_MANAGEMENT_CRUMB, EDC_ROOT_CRUMB } from "../EDCBreadcrumbs";
import StatCard from "../EDCStatCard";
import { EDC_PATHS, requestViewPath } from "../edcPaths";

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj?.[k] != null) return obj[k];
  }
  return undefined;
};

const EDCManagementDashboardPage = () => {
  const [companyId, setCompanyId] = useState(getSelectedDistributionCompanyId);
  const [context, setContext] = useState(null);
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isCompanyAdmin = pick(context, "isCompanyAdmin", "IsCompanyAdmin") === true;
  const canArea =
    isCompanyAdmin || pick(context, "canAreaReview", "CanAreaReview") === true;
  const canEngineering =
    isCompanyAdmin ||
    pick(context, "canEngineeringReview", "CanEngineeringReview") === true;
  const recentTab = canArea ? "area" : "engineering";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const ctxRes = await DistributionReviewApi.getPortalContext(companyId);
      const ctx = ctxRes?.data ?? ctxRes?.Data ?? null;
      setContext(ctx);
      if (!pick(ctx, "distributionCompanyId", "DistributionCompanyId")) {
        setQueue(null);
        return;
      }

      const admin = pick(ctx, "isCompanyAdmin", "IsCompanyAdmin") === true;
      const tab =
        admin || pick(ctx, "canAreaReview", "CanAreaReview") ? "area" : "engineering";
      const queueRes = await DistributionReviewApi.searchPortalQueue({
        tab,
        page: 1,
        pageSize: 5,
        distributionCompanyId: isSystemAdminContext(ctx)
          ? pick(ctx, "distributionCompanyId", "DistributionCompanyId")
          : null,
      });
      setQueue(queueRes?.data ?? queueRes?.Data ?? null);
    } catch (e) {
      setError(e?.message || "خطا در دریافت اطلاعات داشبورد شرکت توزیع");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const areaStats = useMemo(() => {
    const areas = pick(context, "areas", "Areas") || [];
    const activeUserIds = new Set();
    areas.forEach((a) =>
      (pick(a, "experts", "Experts") || [])
        .filter((x) => pick(x, "isActive", "IsActive") !== false)
        .forEach((x) => activeUserIds.add(pick(x, "userId", "UserId")))
    );
    const activeExperts = activeUserIds.size;
    const areasWithoutExpert = areas.filter(
      (a) =>
        !(pick(a, "experts", "Experts") || []).some(
          (x) => pick(x, "isActive", "IsActive") !== false
        )
    ).length;
    return { areaCount: areas.length, activeExperts, areasWithoutExpert };
  }, [context]);

  const quickLinks = useMemo(() => {
    const links = [];
    if (canArea)
      links.push({
        title: "کارتابل امور",
        to: requestViewPath("area"),
        icon: AssignmentOutlinedIcon,
      });
    if (canEngineering)
      links.push({
        title: "واحد مهندسی",
        to: requestViewPath("engineering"),
        icon: EngineeringOutlinedIcon,
      });
    links.push({
      title: "پرونده های رد شده",
      to: requestViewPath("area", "RejectedRequests"),
      icon: BlockOutlinedIcon,
    });
    if (isCompanyAdmin) {
      links.push({
        title: "امور",
        to: EDC_PATHS.areas,
        icon: AccountTreeOutlinedIcon,
      });
      links.push({
        title: "کاربران",
        to: EDC_PATHS.users,
        icon: GroupsOutlinedIcon,
      });
      links.push({
        title: "ایستگاه‌های فوق توزیع",
        to: EDC_PATHS.substations,
        icon: ElectricalServicesOutlinedIcon,
      });
      links.push({
        title: "فیدرها",
        to: EDC_PATHS.feeders,
        icon: CableOutlinedIcon,
      });
      links.push({
        title: "ترانس‌های توزیع",
        to: EDC_PATHS.lvTrans,
        icon: BoltOutlinedIcon,
      });
    }
    return links;
  }, [canArea, canEngineering, isCompanyAdmin]);

  if (!canManageDistributionTeam()) return <Navigate to="/EDC" replace />;
  if (context && !isCompanyAdmin) return <Navigate to="/EDC" replace />;

  const companyName = pick(context, "distributionCompanyName", "DistributionCompanyName");
  const portalReviewEnabled = Boolean(
    pick(context, "portalReviewEnabled", "PortalReviewEnabled")
  );
  const roleLabel = isSystemAdminContext(context) ? "مدیر سیستم" : "مدیر شرکت";
  const recentItems = pick(queue, "items", "Items") || [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <EDCBreadcrumbs crumbs={[EDC_ROOT_CRUMB, EDC_MANAGEMENT_CRUMB]} />
      </Grid>

      <Grid item xs={12}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              داشبورد مدیریت شرکت توزیع
            </Typography>
            {context ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {companyName || "—"}
                </Typography>
                <Chip size="small" label={roleLabel} />
              </Stack>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <DistributionCompanySelect context={context} onChange={setCompanyId} />
            <Button variant="outlined" size="small" onClick={load} disabled={loading}>
              به‌روزرسانی
            </Button>
          </Stack>
        </Stack>
      </Grid>

      {error ? (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      ) : null}

      {context && !portalReviewEnabled ? (
        <Grid item xs={12}>
          <Alert severity="warning">
            چرخه کارتابل این شرکت توزیع در سولارنت هنوز فعال نشده است؛ تا زمان
            فعال‌سازی توسط سولارنت (طبق توافق)، درخواستی به کارتابل امور ارجاع
            نمی‌شود.
          </Alert>
        </Grid>
      ) : null}

      {loading && !context ? (
        <Grid item xs={12}>
          <Typography color="text.secondary">در حال بارگذاری…</Typography>
        </Grid>
      ) : null}

      {context ? (
        <>
          {canArea ? (
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon={AssignmentOutlinedIcon}
                label="در صف بررسی امور"
                value={pick(queue, "areaQueueCount", "AreaQueueCount")}
                color="warning"
                to={requestViewPath("area")}
              />
            </Grid>
          ) : null}
          {canEngineering ? (
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon={EngineeringOutlinedIcon}
                label="در صف واحد مهندسی"
                value={pick(queue, "engineeringQueueCount", "EngineeringQueueCount")}
                color="info"
                to={requestViewPath("engineering")}
              />
            </Grid>
          ) : null}
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard
              icon={PriceCheckOutlinedIcon}
              label="تأییدشده، منتظر قیمت نهایی"
              value={pick(queue, "readyForPricingCount", "ReadyForPricingCount")}
              color="success"
            />
          </Grid>
          {isCompanyAdmin ? (
            <>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <StatCard
                  icon={AccountTreeOutlinedIcon}
                  label="امور تعریف‌شده"
                  value={areaStats.areaCount}
                  to={EDC_PATHS.areas}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <StatCard
                  icon={areaStats.areasWithoutExpert > 0 ? WarningAmberOutlinedIcon : GroupsOutlinedIcon}
                  label="کاربران فعال امور"
                  value={areaStats.activeExperts}
                  color={areaStats.areasWithoutExpert > 0 ? "error" : "secondary"}
                  hint={
                    areaStats.areasWithoutExpert > 0
                      ? `${areaStats.areasWithoutExpert} امور بدون کاربر — پیامک ارجاع ارسال نمی‌شود`
                      : null
                  }
                  to={EDC_PATHS.users}
                />
              </Grid>
            </>
          ) : null}

          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardHeader
                title={
                  recentTab === "area"
                    ? "آخرین پرونده‌های منتظر بررسی امور"
                    : "آخرین پرونده‌های منتظر واحد مهندسی"
                }
                action={
                  <Button
                    component={RouterLink}
                    to={requestViewPath(recentTab)}
                    size="small"
                  >
                    مشاهده کارتابل
                  </Button>
                }
              />
              <CardContent>
                {recentItems.length === 0 ? (
                  <Typography color="text.secondary">
                    پرونده‌ای در انتظار بررسی نیست.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {recentItems.map((row) => (
                      <Stack
                        key={pick(row, "id", "Id")}
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1 }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            PRJ-{pick(row, "referenceId", "ReferenceId")} —{" "}
                            {pick(row, "applicantName", "ApplicantName") || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            امور: {pick(row, "distributionAreaName", "DistributionAreaName") || "—"}
                          </Typography>
                        </Box>
                        <Chip size="small" label={pick(row, "stageTitle", "StageTitle") || "—"} />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardHeader title="دسترسی سریع" />
              <CardContent>
                <Stack spacing={1}>
                  {quickLinks.map((link) => {
                    const LinkIcon = link.icon;
                    return (
                      <Button
                        key={link.to}
                        component={RouterLink}
                        to={link.to}
                        variant="outlined"
                        startIcon={<LinkIcon />}
                        sx={{ justifyContent: "flex-start" }}
                        fullWidth
                      >
                        {link.title}
                      </Button>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </>
      ) : null}
    </Grid>
  );
};

export default EDCManagementDashboardPage;
